import { DeviceType } from '@services/constants'
import {
  ChargingPointDetailStatusColor,
  EStatusPill,
  IStatisticalByDeviceType,
  useSessionDeviceStatistical,
} from '@services/device'
import { toThousandSeparator } from '@shared/innmaxLib/utils/webHelper'
import moment from 'moment'
import { pluck, sum } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDashboardContext } from '../Desktop/Provider'

type DevicesBoardType = {
  lastUpdated: moment.Moment
  onSelect?: () => void
}

export default function SimpleDevicesBoard({
  lastUpdated,
  onSelect,
}: DevicesBoardType) {
  const { t } = useTranslation()

  const { refetch, isLoading, data } = useSessionDeviceStatistical()

  const { baseLayer, setFilterVariables, filterVariables } =
    useDashboardContext()

  React.useEffect(() => {
    if (!baseLayer?.id) {
      return
    }
    refetch({
      sessionId: baseLayer?.id,
      deep: true,
      isPositioned: true,
      cacheKey: 'deviceBoard',
    })
  }, [lastUpdated, baseLayer]) //eslint-disable-line

  const sumConnectorDetail = (...keys: (keyof IStatisticalByDeviceType)[]) => {
    const connectorData =
      data?.filter(item => item?.deviceType === DeviceType.CONNECTOR) || []
    const total = keys.reduce((acc, key) => {
      return acc + sum(pluck(key as any, connectorData))
    }, 0)
    return toThousandSeparator(total)
  }

  const chargingpointCount = toThousandSeparator(
    sum(
      pluck(
        'deviceCount',
        data?.filter(item => item.deviceType === DeviceType.CHARGINGPOINT) || []
      )
    )
  )

  return (
    <StyledBoard>
      <div className="bg-[rgba(255,255,255,0.1)] flex-1 rounded min-h-55 min-w-72 text-primary break-keep text-center p-5">
        <div className="text-sm font-medium">充電樁數</div>
        <div className="text-[24px] font-semibold">{chargingpointCount}</div>
      </div>

      {[
        {
          title: '可使用',
          value: sumConnectorDetail('availableCount'),
          backgroundColor:
            ChargingPointDetailStatusColor[EStatusPill.Available],
        },
        {
          title: '充電中',
          value: sumConnectorDetail('chargingCount'),
          backgroundColor: ChargingPointDetailStatusColor[EStatusPill.Charging],
        },
        {
          title: '故障',
          value: sumConnectorDetail('faultedCount'),
          backgroundColor: ChargingPointDetailStatusColor[EStatusPill.Faulted],
        },
        {
          title: '占用中',
          value: sumConnectorDetail('finishingCount'),
          backgroundColor: ChargingPointDetailStatusColor[EStatusPill.Occupy],
        },
        {
          title: '無服務',
          value: sumConnectorDetail('unavailableCount'),
          backgroundColor:
            ChargingPointDetailStatusColor[EStatusPill.Unavailable],
        },
      ].map(item => (
        <div
          key={item.title}
          className="text-white flex-1 text-sm font-medium text-center min-w-40">
          <div
            style={{
              backgroundColor: item.backgroundColor,
            }}
            className="rounded h-25 text-base flex items-center justify-center">
            <div>{item.value}</div>
          </div>
          <div className="mt-5">{item.title}</div>
        </div>
      ))}
    </StyledBoard>
  )
}

const StyledBoard = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  margin-top: 15px;
  gap: 10px;
  padding: 0 15px;
`
